/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

import {Box} from '@primer/react';
import React from 'react';

/**
 * Skeleton that mimics GitHub's diff loading state: a series of
 * shimmer-animated bars resembling line-number gutters and code lines.
 *
 * When `lineCount` is provided (from the PR file stats), the skeleton renders
 * the correct number of rows so the page height stays stable as diffs load.
 * When absent, falls back to a default of 8 rows.
 */

// Predefined widths that cycle to look like realistic code lines
const LINE_WIDTHS = ['72%', '45%', '88%', '60%', '35%', '78%', '50%', '65%'];

/**
 * Height of a single diff row in pixels.
 * Matches the CSS: 16px line-height + 2px top padding + 2px bottom padding = 20px.
 */
const DIFF_ROW_HEIGHT_PX = 20;

/** Default number of skeleton rows when we don't know the actual line count. */
const DEFAULT_LINE_COUNT = 8;

/** Maximum skeleton rows to prevent absurdly tall skeletons for huge files. */
const MAX_SKELETON_LINES = 200;

const shimmerBarSx = {
  background:
    'linear-gradient(90deg, var(--bgColor-neutral-muted, #656d7611) 25%, var(--bgColor-muted, #656d7622) 50%, var(--bgColor-neutral-muted, #656d7611) 75%)',
  backgroundSize: '400px 100%',
  animation: 'shimmer 1.5s ease-in-out infinite',
};

type Props = {
  /** Estimated number of diff lines to render. When provided, the skeleton
   *  is sized to match the expected diff height (monospace rows). */
  lineCount?: number;
};

export default function DiffContentSkeleton({lineCount}: Props): React.ReactElement {
  const count = Math.min(
    Math.max(lineCount ?? DEFAULT_LINE_COUNT, 1),
    MAX_SKELETON_LINES,
  );

  // For small counts, render individual shimmer bars for visual fidelity.
  // For large counts, render a fixed height container with a few shimmer bars
  // at the top so we don't create hundreds of DOM nodes.
  const INLINE_THRESHOLD = 30;
  const useCompact = count > INLINE_THRESHOLD;

  if (useCompact) {
    const totalHeight = count * DIFF_ROW_HEIGHT_PX;
    // For large skeletons, use a repeating CSS background pattern that tiles
    // shimmer bars across the full container height. This avoids creating
    // hundreds of DOM nodes while filling the entire visible area.
    //
    // We create one background layer per line-width variant (8 total for gutter
    // + 8 for code). Each layer uses a vertical gradient that draws a single
    // 12px-tall bar within a 160px (8 × 20px row) tile. The bar is positioned
    // at its row's y-offset within the tile, and the tile repeats vertically.
    const barColor = 'var(--bgColor-neutral-muted, #656d7611)';
    const transparent = 'transparent';
    const tileHeight = DIFF_ROW_HEIGHT_PX * LINE_WIDTHS.length; // 160px

    const layers: string[] = [];
    const sizes: string[] = [];
    const positions: string[] = [];

    for (let i = 0; i < LINE_WIDTHS.length; i++) {
      // Within the tile, this row's bar starts at yStart and is 12px tall
      const yStart = i * DIFF_ROW_HEIGHT_PX + 4;
      const yEnd = yStart + 12;
      // Gradient that is transparent except for a 12px band
      const grad = `linear-gradient(to bottom, ${transparent} ${yStart}px, ${barColor} ${yStart}px, ${barColor} ${yEnd}px, ${transparent} ${yEnd}px)`;

      // Gutter bar: 28px wide at x=12
      layers.push(grad);
      sizes.push(`28px ${tileHeight}px`);
      positions.push(`12px 0`);

      // Code bar: variable width at x=48
      layers.push(grad);
      sizes.push(`calc(${LINE_WIDTHS[i]} - 48px) ${tileHeight}px`);
      positions.push(`48px 0`);
    }

    const style: React.CSSProperties = {
      height: totalHeight,
      backgroundImage: layers.join(', '),
      backgroundSize: sizes.join(', '),
      backgroundPosition: positions.join(', '),
      backgroundRepeat: 'repeat-y',
    };

    return <div style={style} />;
  }

  return (
    <Box
      padding={3}
      display="flex"
      flexDirection="column"
      sx={{
        gap: '6px',
        '@keyframes shimmer': {
          '0%': {backgroundPosition: '-200px 0'},
          '100%': {backgroundPosition: '200px 0'},
        },
      }}>
      {Array.from({length: count}, (_, i) => (
        <Box key={i} display="flex" alignItems="center" sx={{gap: '8px'}}>
          <Box height={12} width={28} borderRadius={1} sx={shimmerBarSx} />
          <Box height={12} width={LINE_WIDTHS[i % LINE_WIDTHS.length]} borderRadius={1} sx={shimmerBarSx} />
        </Box>
      ))}
    </Box>
  );
}
